import React, { useCallback } from 'react';

import {
  Clear as ClearIcon,
  Error as FailedIcon,
  HourglassEmpty as PendingIcon,
  CheckCircle as ProcessedIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';

import { type ManualAccrualStatus, PRODUCT_TYPE_OPTIONS, type ProductType } from '~/types/loyaltyContract';

interface ManualAccrualsSearchFiltersSectionProps {
  themeColor: string;
  // Filter values
  customerIdInput: string;
  orderIdInput: string;
  itemIdInput: string;
  selectedProductType: ProductType | '';
  selectedStatus: ManualAccrualStatus | 'all';

  // Setters
  setCustomerIdInput: (value: string) => void;
  setOrderIdInput: (value: string) => void;
  setItemIdInput: (value: string) => void;
  setSelectedProductType: (value: ProductType | '') => void;
  setSelectedStatus: (value: ManualAccrualStatus | 'all') => void;

  // Actions
  onSearch: () => void;
  onClear: () => void;
}

export default function ManualAccrualsSearchFiltersSection({
  themeColor,
  customerIdInput,
  orderIdInput,
  itemIdInput,
  selectedProductType,
  selectedStatus,
  setCustomerIdInput,
  setOrderIdInput,
  setItemIdInput,
  setSelectedProductType,
  setSelectedStatus,
  onSearch,
  onClear,
}: ManualAccrualsSearchFiltersSectionProps) {
  const theme = useTheme();

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      onSearch();
    },
    [onSearch],
  );

  const inputStyles = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
    },
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 3,
        bgcolor: alpha(theme.palette.background.paper, 0.6),
        backdropFilter: 'blur(10px)',
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
      }}
    >
      <Box component="form" onSubmit={handleSubmit}>
        <Stack spacing={2}>
          {/* Row 1: Customer ID + Order ID */}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              label="Customer ID"
              value={customerIdInput}
              onChange={(e) => setCustomerIdInput(e.target.value)}
              placeholder="Search by customer ID"
              fullWidth
              size="small"
              sx={inputStyles}
            />
            <TextField
              label="Order ID"
              value={orderIdInput}
              onChange={(e) => setOrderIdInput(e.target.value)}
              placeholder="Search by order ID"
              fullWidth
              size="small"
              sx={inputStyles}
            />
          </Stack>

          {/* Row 2: Item ID + Product Type */}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              label="Item ID"
              value={itemIdInput}
              onChange={(e) => setItemIdInput(e.target.value)}
              placeholder="Search by item ID"
              fullWidth
              size="small"
              sx={inputStyles}
            />
            <FormControl fullWidth size="small">
              <InputLabel>Product Type</InputLabel>
              <Select
                value={selectedProductType}
                label="Product Type"
                onChange={(e: SelectChangeEvent<ProductType | ''>) =>
                  setSelectedProductType(e.target.value as ProductType | '')
                }
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">
                  <Typography sx={{ fontStyle: 'italic', color: 'text.secondary' }}>All</Typography>
                </MenuItem>
                {PRODUCT_TYPE_OPTIONS.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          {/* Row 3: Status Filter */}
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: theme.typography.fontWeightMedium }}>
              Status:
            </Typography>
            <ToggleButtonGroup
              value={selectedStatus}
              exclusive
              onChange={(_e, newValue) => {
                if (newValue !== null) {
                  setSelectedStatus(newValue as ManualAccrualStatus | 'all');
                }
              }}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  px: 2,
                  '&.Mui-selected': {
                    bgcolor: alpha(themeColor, 0.15),
                    color: themeColor,
                    fontWeight: theme.typography.fontWeightBold,
                    '&:hover': {
                      bgcolor: alpha(themeColor, 0.25),
                    },
                  },
                  '&:hover': {
                    bgcolor: alpha(themeColor, 0.05),
                  },
                },
                '& .MuiToggleButton-root:first-of-type': {
                  borderTopLeftRadius: 8,
                  borderBottomLeftRadius: 8,
                },
                '& .MuiToggleButton-root:last-of-type': {
                  borderTopRightRadius: 8,
                  borderBottomRightRadius: 8,
                },
              }}
            >
              <ToggleButton value="all">All</ToggleButton>
              <ToggleButton value="PENDING">
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <PendingIcon sx={{ fontSize: theme.typography.body2.fontSize, color: theme.palette.warning.main }} />
                  <span>Pending</span>
                </Stack>
              </ToggleButton>
              <ToggleButton value="PROCESSED">
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <ProcessedIcon
                    sx={{ fontSize: theme.typography.body2.fontSize, color: theme.palette.success.main }}
                  />
                  <span>Processed</span>
                </Stack>
              </ToggleButton>
              <ToggleButton value="FAILED">
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <FailedIcon sx={{ fontSize: theme.typography.body2.fontSize, color: theme.palette.error.main }} />
                  <span>Failed</span>
                </Stack>
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          {/* Row 4: Action buttons */}
          <Stack direction="row" spacing={1.5} justifyContent="flex-start">
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={<SearchIcon />}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                minWidth: 110,
                height: 40,
                backgroundColor: themeColor,
                '&:hover': {
                  backgroundColor: alpha(themeColor, 0.8),
                },
              }}
            >
              Search
            </Button>
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={onClear}
              sx={{
                color: themeColor,
                borderRadius: 2,
                textTransform: 'none',
                minWidth: 90,
                height: 40,
                border: `1px solid ${themeColor}`,
                '&:hover': {
                  border: `1px solid ${themeColor}`,
                  backgroundColor: alpha(themeColor, 0.1),
                },
              }}
            >
              Clear
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Paper>
  );
}
